import * as THREE from 'three';

const FABRIC = new Set(['cloth', 'cloth_dark', 'leather', 'wood', 'dark']);
const METAL = new Set(['metal', 'rust', 'alloy', 'gold', 'trim']);
type Wear = 'metal' | 'fabric' | 'skin';

/** Which wear a surface takes, by its material's name; none for the rest. */
function wearOf(material: THREE.Material): Wear | undefined {
  if (!(material instanceof THREE.MeshStandardMaterial)) return undefined;
  return METAL.has(material.name) ? 'metal' : FABRIC.has(material.name) ? 'fabric' : material.name === 'skin' ? 'skin' : undefined;
}

/** Dulls a worn material's finish, once per pass however many surfaces share it. */
function wearMaterial(material: THREE.MeshStandardMaterial, wear: Wear, finished: Set<THREE.Material>) {
  if (finished.has(material)) return;
  if (wear === 'metal') {
    material.metalness = Math.min(material.metalness, .48);
    material.roughness = Math.max(material.roughness, .76);
    if (material.name === 'gold') material.color.setHex(0x827050);
  } else if (wear === 'fabric') {
    material.roughness = Math.max(material.roughness, .94);
    if (material.name === 'cloth') material.color.lerp(new THREE.Color(0x75654f), .20).multiplyScalar(.78);
  } else material.roughness = Math.max(material.roughness, .83);
  material.vertexColors = true;
  finished.add(material);
}

/** Mottling, grain and a darker hem, measured over `box` (the whole mesh the geometry is drawn in). */
function wearGeometry(geometry: THREE.BufferGeometry, wear: Wear, box: THREE.Box3) {
  const position = geometry.getAttribute('position'), normal = geometry.getAttribute('normal');
  const previous = geometry.getAttribute('color'), colors = new Float32Array(position.count * 3);
  const height = Math.max(.01, box.max.y - box.min.y), skin = wear === 'skin';
  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i), y = position.getY(i), z = position.getZ(i);
    // Continuous in bind space: duplicated seams get the same grain, which travels with the skin.
    const mottling = Math.sin(x * 17 + y * 11 + z * 9) * Math.sin(y * 23 - z * 15 + x * 7);
    const grain = Math.sin(x * 191 + y * 137 + z * 173) * .018;
    const hem = 1 - THREE.MathUtils.smoothstep((y - box.min.y) / height, .02, .24);
    const rubbed = wear === 'metal' && normal ? Math.abs(normal.getY(i)) * .07 : 0;
    const tone = (skin ? .95 : .84) + mottling * (skin ? .045 : .085) + grain + rubbed - hem * (skin ? .025 : .10);
    colors[i * 3] = (previous?.getX(i) ?? 1) * tone;
    colors[i * 3 + 1] = (previous?.getY(i) ?? 1) * tone * (skin ? 1 : .985);
    colors[i * 3 + 2] = (previous?.getZ(i) ?? 1) * tone * (skin ? 1 : .95);
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

/** Bake wear into existing surfaces once; no textures, extra passes or frame updates. `finished` carries over from `wearSurface`. */
export function finishWornSurfaces(root: THREE.Object3D, finished = new Set<THREE.Material>()) {
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh) || Array.isArray(object.material)) return;
    const wear = wearOf(object.material);
    if (!wear) return;
    wearMaterial(object.material, wear, finished);
    object.geometry.computeBoundingBox();
    wearGeometry(object.geometry, wear, object.geometry.boundingBox!);
  });
}

/**
 * The same wear for a surface's pieces before they are merged with other surfaces into one draw: the hem is measured over all of them, as
 * it would be over the surface's own merged mesh. Pass the same `finished` to the `finishWornSurfaces` that follows.
 */
export function wearSurface(material: THREE.Material, geometries: readonly THREE.BufferGeometry[], finished: Set<THREE.Material>) {
  const wear = wearOf(material);
  if (!wear) return;
  wearMaterial(material as THREE.MeshStandardMaterial, wear, finished);
  const box = new THREE.Box3();
  for (const geometry of geometries) { geometry.computeBoundingBox(); box.union(geometry.boundingBox!); }
  for (const geometry of geometries) wearGeometry(geometry, wear, box);
}
